import React from 'react'
import ReactDOM from 'react-dom/client'
import * as Sentry from '@sentry/react'
import App from './App'
import './index.css'
import { ToastProvider } from './components/ToastProvider'
import AppErrorBoundary from './components/AppErrorBoundary'
import { registerServiceWorker } from './services/notificationService'
import { initBootDiagnostics, installBootDiagnosticsGlobal, reportBootEvent } from './utils/bootDiagnostics'
import { shouldRegisterServiceWorker } from './utils/inAppBrowser'
import { autoReloadWithBudget, getReloadAttempt } from './utils/reloadGuard'

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE || 'production',
    tracesSampleRate: 0.1,
    // Туннель: ошибки летят на свой сервер (обходит блокировки РФ, adblock, CSP)
    tunnel: '/sentry-tunnel',
  })
}

installBootDiagnosticsGlobal()
initBootDiagnostics()

function showBootError(message) {
  const root = document.getElementById('root')
  if (!root) return
  document.documentElement.dataset.appReady = 'error'
  root.innerHTML = `
    <div class="boot-error">
      <p class="boot-error-title">Не удалось загрузить приложение</p>
      <p class="boot-error-text">${message}</p>
      <button type="button" class="boot-error-btn" onclick="window.location.href=window.location.pathname+'?_cb='+Date.now()">Перезагрузить</button>
    </div>
  `
}

function isChunkLoadError(message) {
  if (!message) return false
  const m = String(message).toLowerCase()
  return m.includes('failed to fetch dynamically imported module')
    || m.includes('loading chunk')
    || m.includes('importing a module script failed')
    || m.includes('error loading dynamically imported module')
}

// Обезличенная "Script error." без деталей — это ошибка cross-origin скрипта
// (расширения браузера, встроенные браузеры вроде YaBrowser/Telegram, сторонние
// инжекты). Она НЕ означает сбой загрузки нашего приложения и не должна рушить
// уже работающий UI. Раньше такой случай ошибочно показывал экран ошибки поверх
// смонтированного приложения → «белый экран» на iOS (подтверждено диагностикой).
function isOpaqueCrossOriginError(message) {
  if (!message) return true // iOS/WebKit: cross-origin ошибка без сообщения
  return String(message).trim().toLowerCase() === 'script error.'
}

window.addEventListener('error', (event) => {
  const target = event.target
  const appReady = document.documentElement.dataset.appReady === '1'

  // Реальный сбой загрузки ресурса (<script>/<link> вернул 404 и т.п.).
  if (target?.tagName === 'SCRIPT' || target?.tagName === 'LINK') {
    if (appReady) {
      // Приложение уже работает — единичный сбой подгрузки ресурса не должен
      // его рушить (это может быть некритичный шрифт/префетч).
      reportBootEvent('asset_error_after_ready', {
        tag: target.tagName,
        src: target?.src || target?.href || null,
      })
      return
    }
    // На этапе загрузки сбой entry/chunk — настоящая причина белого экрана.
    attemptAssetReload(event)
    return
  }

  // Явная ошибка загрузки чанка (динамический импорт устаревшего бандла).
  if (isChunkLoadError(event.message)) {
    if (!appReady) {
      attemptAssetReload(event)
      return
    }
    reportBootEvent('chunk_load_error', { message: event.message })
    showBootError('Устаревшая версия приложения в кэше. Нажмите «Перезагрузить».')
    return
  }

  // Обезличенная cross-origin ошибка: НИКОГДА не считаем её фатальной.
  // Не трогаем работающее приложение; на этапе загрузки полагаемся на
  // вотчдоги (index.html + useAppBootMonitor), а не на снос UI.
  if (isOpaqueCrossOriginError(event.message)) {
    reportBootEvent('opaque_script_error', {
      appReady,
      filename: event?.filename || null,
    })
  }
})

function attemptAssetReload(event) {
  document.documentElement.dataset.assetReloaded = '1'
  // Дополнительно пишем в sessionStorage (если доступен) — для диагностики
  try { sessionStorage.setItem('asset_auto_reload_once', '1') } catch { /* ignore */ }

  // Счётчик попыток живёт в URL (_boot) и переживает перезагрузку, поэтому
  // на iOS не возникает бесконечного цикла белых перезагрузок при устаревшем
  // index.html. Когда бюджет исчерпан — показываем экран восстановления.
  if (!autoReloadWithBudget()) {
    reportBootEvent('asset_load_error', {
      tag: event?.target?.tagName,
      src: event?.target?.src || event?.target?.href || null,
      attempts: getReloadAttempt(),
      budgetExhausted: true,
    })
    showBootError('Не удалось загрузить файлы приложения. Проверьте интернет и нажмите «Перезагрузить».')
  }
}

// iOS Safari: динамические импорты могут падать как unhandledrejection
// при кэшированном index.html, ссылающемся на старые чанки
window.addEventListener('unhandledrejection', (event) => {
  const msg = event.reason?.message || String(event.reason || '')
  if (isChunkLoadError(msg)) {
    event.preventDefault()
    if (!document.documentElement.dataset.appReady) {
      attemptAssetReload(event)
      return
    }
    reportBootEvent('chunk_load_error', { message: msg })
    showBootError('Устаревшая версия приложения в кэше. Нажмите «Перезагрузить».')
  }
})

// iOS Safari / home screen: восстановление из BFCache с «мёртвым» JS
// iOS 17+ может игнорировать location.reload() внутри pageshow.
// Используем replace + cache-buster — надёжнее.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    // Через общий бюджет — чтобы восстановление из BFCache тоже не зациклилось.
    autoReloadWithBudget()
  }
})

if (shouldRegisterServiceWorker()) {
  registerServiceWorker()
} else {
  reportBootEvent('boot_start', { serviceWorker: 'skipped_in_app_browser' })
}

const rootEl = document.getElementById('root')
if (!rootEl) {
  console.error('[boot] #root not found')
} else {
  try {
    ReactDOM.createRoot(rootEl).render(
      <React.StrictMode>
        <Sentry.ErrorBoundary fallback={({ error, resetError }) => {
          console.error('[Sentry.ErrorBoundary]', error)
          // ВАЖНО: не возвращаем null — иначе #root будет пустым = белый экран.
          // Показываем UI восстановления, даже если AppErrorBoundary не сработал.
          return (
            <div style={{
              minHeight: '100dvh', minHeight: '100svh',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 24, boxSizing: 'border-box',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              fontFamily: 'system-ui, -apple-system, sans-serif',
            }}>
              <div style={{
                maxWidth: 360, width: '100%', background: '#fff',
                borderRadius: 24, padding: '28px 24px', textAlign: 'center',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
              }}>
                <p style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 800, color: '#1e293b' }}>
                  Не удалось загрузить приложение
                </p>
                <p style={{ margin: '0 0 20px', fontSize: 13, lineHeight: 1.5, color: '#64748b' }}>
                  Критическая ошибка. Пожалуйста, перезагрузите страницу.
                </p>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  style={{
                    width: '100%', padding: '14px 20px', border: 'none',
                    borderRadius: 16, background: '#2563eb', color: '#fff',
                    fontSize: 14, fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  Перезагрузить
                </button>
              </div>
            </div>
          )
        }}>
          <AppErrorBoundary>
            <ToastProvider>
              <App />
            </ToastProvider>
          </AppErrorBoundary>
        </Sentry.ErrorBoundary>
      </React.StrictMode>,
    )
  } catch (error) {
    console.error('[boot] React mount failed:', error)
    reportBootEvent('react_mount_failed', { message: error?.message || String(error) })
    showBootError('Ошибка запуска приложения. Нажмите «Перезагрузить».')
  }
}
